import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LuMessageCircle, LuSend, LuChevronDown } from "react-icons/lu";
import { FaRegCircleXmark } from "react-icons/fa6";

import "./styles.scss";
import { SupportChatQueryAction, SupportChatQueryReset } from "../../../store/slices/support-chat/query";

const extractErrorMessage = (error) => {
    if (!error) return "";
    if (typeof error === "string") return error;
    return error?.message || error?.error || "Something went wrong. Please try again.";
};

const SupportChatWidget = ({ userEmail, userName }) => {
    
    const [isOpen, setIsOpen] = useState(false);
    const [subjectText, setSubjectText] = useState("");
    const [queryText, setQueryText] = useState("");
    const [screenshots, setScreenshots] = useState([]);
    
    const { isLoading, error, success } = useSelector((state) => state.supportChatQuery);
    
    const dispatch = useDispatch();
    const screenshotsRef = useRef([]);
    useEffect(() => {
        screenshotsRef.current = screenshots;
    }, [screenshots]);

    const clearScreenshots = () => {
        screenshotsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setScreenshots([]);
    };

    const resetFlow = () => {
        setSubjectText("");
        setQueryText("");
        clearScreenshots();
        dispatch(SupportChatQueryReset());
    };

    const clearFeedbackIfAny = () => {
        if (success || error) dispatch(SupportChatQueryReset());
    };

    const handleToggleOpen = () => {
        if (isOpen) {
            resetFlow();
            setIsOpen(false);
        } else {
            setIsOpen(true);
        }
    };

    const handleSubjectChange = (e) => {
        clearFeedbackIfAny();
        setSubjectText(e.target.value);
    };

    const handleQueryChange = (e) => {
        clearFeedbackIfAny();
        setQueryText(e.target.value);
    };

    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items?.length) return;

        const newScreenshots = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type?.startsWith("image/")) {
                const file = item.getAsFile();
                if (file) {
                    newScreenshots.push({
                        id: `${Date.now()}-${i}`,
                        file,
                        previewUrl: URL.createObjectURL(file),
                    });
                }
            }
        }

        if (newScreenshots.length) {
            e.preventDefault();
            clearFeedbackIfAny();
            setScreenshots((prev) => [...prev, ...newScreenshots]);
        }
    };

    const handleRemoveScreenshot = (id) => {
        clearFeedbackIfAny();
        setScreenshots((prev) => {
            const target = prev.find((item) => item.id === id);
            if (target) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((item) => item.id !== id);
        });
    };

    const handleSubmit = () => {
        const trimmedQuery = queryText.trim();

        if ((!trimmedQuery && !screenshots.length) || isLoading) return;

        const formData = new FormData();
        formData.append("subject", subjectText.trim());
        formData.append("query", trimmedQuery);
        if (userEmail) formData.append("email", userEmail);
        if (userName) formData.append("name", userName);
        screenshots.forEach((item) => formData.append("screenshot", item.file));

        dispatch(SupportChatQueryAction(formData));
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    useEffect(() => {
        if (success) {
            setSubjectText("");
            setQueryText("");
            clearScreenshots();
        }
    }, [success]);

    useEffect(() => {
        return () => {
            screenshotsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        };
    }, []);

    const isSendDisabled = isLoading || (!queryText.trim() && !screenshots.length);

    return (
        <div className="support-chat-widget">
            {isOpen && (
                <div className="support-chat-panel">
                    <div className="support-chat-header">
                        <span className="support-chat-title">Contact Support</span>
                        <FaRegCircleXmark className="support-chat-close-icon" onClick={handleToggleOpen} />
                    </div>
                    <div className="support-chat-body">
                        {success && (
                            <div className="support-chat-success-message">
                                Thanks for contacting support team. We will respond back shortly
                            </div>
                        )}
                        {error && (
                            <div className="support-chat-error-message">
                                {extractErrorMessage(error)}
                            </div>
                        )}
                        {!!screenshots.length && (
                            <div className="support-chat-screenshot-list">
                                {screenshots.map((item) => (
                                    <div className="support-chat-screenshot-preview" key={item.id}>
                                        <img src={item.previewUrl} alt="Pasted screenshot" />
                                        <span className="remove-screenshot" onClick={() => handleRemoveScreenshot(item.id)}>
                                            <FaRegCircleXmark />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <input
                            type="text"
                            className="support-chat-subject-input"
                            placeholder="Subject"
                            value={subjectText}
                            onChange={handleSubjectChange}
                            disabled={isLoading}
                        />
                        <div className="support-chat-input-wrapper">
                            <textarea
                                className="support-chat-input"
                                placeholder="Type your query, or paste a screenshot..."
                                value={queryText}
                                onChange={handleQueryChange}
                                onPaste={handlePaste}
                                onKeyDown={handleKeyPress}
                                disabled={isLoading}
                            />
                            <div className={`support-chat-send-button ${isSendDisabled ? "disabled" : ""}`} onClick={handleSubmit}>
                                <LuSend />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="support-chat-trigger" onClick={handleToggleOpen}>
                {isOpen ? <LuChevronDown /> : <LuMessageCircle />}
            </div>
        </div>
    );
};

export default SupportChatWidget;
